import * as React from "react";
import { connect } from "react-redux";

import { isRequestActive, LoadingState } from "redux-request-loading";
import { Container, Item, Loader, Form } from "semantic-ui-react";

import { Input } from "@neworbit/simpleui-input";

import { PRODUCT_TYPES } from "@teamprevent/common/model";
import { TaskModel } from "@teamprevent/common/task/taskActions/models";
import { DropdownOption } from "@teamprevent/common/questionnaires/model";
import { QuestionnairesState } from "@teamprevent/common/questionnaires/model";

import { ThunkDispatch } from "redux-thunk";

import { Action } from "redux";

import { loadDetailedOhCases, OhCaseState } from "../../crud/ohCase";
import { EmployeeRecordState } from "../../crud/employeeRecord";
import { OhCaseDocument, OhCase } from "../../crud/ohCase/model";
import { EmployeeDocument } from "../../crud/employeeRecord/model";
import { LOAD_OHCASE_DETAILS } from "../../crud/ohCase/actiontypes";
import { LOAD_EMPLOYEE_DOCUMENTS } from "../../crud/employeeRecord/actiontypes";
import { TaskCategory, TaskCategoriesState } from "../../taskCategories/model";
import { AdminUser, AdminUserState, loadAdminUsers } from "../../crud/adminUser";
import { mapAdminUsersToOptions } from "../../crud/adminUser/useAdminUserOptions";

import { loadEmployeeDocuments } from "../../crud/employeeRecord/actions";

import { loadTaskCategories } from "../../taskCategories";

import { CaseTaskCard } from "./CaseTaskCard";
import { DocumentCard } from "./DocumentCard";

import { State as RouterState, Link } from "redux-little-router";

interface ClinicalHistoryStateProps {
    ohCases: (OhCase & { documents?: OhCaseDocument[] })[];
    employeeDocuments: EmployeeDocument[];
    customerId: string;
    employeeRecordId: string;
    isLoading: boolean;
    taskCategories: TaskCategory[];
    adminUsers: AdminUser[];
}

interface ClinicalHistoryState {
    selectedTaskCategories: string[];
    selectedCaseTypes: string[];
}

export interface DispatchProps {
    loadDetailedOhCases: (customerId: string, employeeRecordId: string) => void;
    loadEmployeeDocuments: (customerId: string, employeeRecordId: string) => void;
    loadTaskCategories: () => void;
    loadAdminUsers: () => void;
}

const documentsFilterKey = "documents";

class ClinicalHistoryUnconnected extends React.Component<ClinicalHistoryStateProps, ClinicalHistoryState, DispatchProps> {
    constructor(props: ClinicalHistoryStateProps & DispatchProps) {
        super(props);
        this.renderCaseDocument = this.renderCaseDocument.bind(this);
        this.renderEmployeeDocument = this.renderEmployeeDocument.bind(this);
        this.renderTask = this.renderTask.bind(this);
        this.state = { selectedTaskCategories: [], selectedCaseTypes: [] };

        props.loadDetailedOhCases(props.customerId, props.employeeRecordId);
        props.loadEmployeeDocuments(props.customerId, props.employeeRecordId);
        props.loadTaskCategories();
        props.loadAdminUsers();
    }

    public render() {
        const ohCases = this.props.ohCases
            .filter(c => this.state.selectedCaseTypes.length === 0 || this.state.selectedCaseTypes.indexOf(c.caseType) > -1);
        const includeDocuments = this.state.selectedTaskCategories.length === 0 || this.state.selectedTaskCategories.indexOf(documentsFilterKey) > -1;
        const taskTypes = this.state.selectedTaskCategories
            .reduce((x, s) => x.concat((this.props.taskCategories.find(c => c.name === s) || { taskTypes: [] }).taskTypes), []);
        const adminUserOptions = mapAdminUsersToOptions(this.props.adminUsers);
        const tasks = ohCases
            .map(c =>
                (c.tasks || [])
                    .filter(t => this.state.selectedTaskCategories.length === 0 || taskTypes.indexOf(t.type) > -1)
                    .map(t => ({ dateCreated: t.dateCreated, render: this.renderTask(t, c, adminUserOptions) }))
            )
            .reduce((a, b) => a.concat(b), []);
        const caseDocuments = includeDocuments ?
            ohCases
                .map(c => (c.documents || []).map(d => ({ dateCreated: d.dateCreated, render: this.renderCaseDocument(d, c) })))
                .reduce((a, b) => a.concat(b), [])
            : [];
        const employeeDocuments = includeDocuments && this.state.selectedCaseTypes.length === 0 ?
            this.props.employeeDocuments.map(d => ({ dateCreated: d.dateCreated, render: this.renderEmployeeDocument(d) }))
            : [];
        const tasksAndDocuments = [...tasks, ...caseDocuments, ...employeeDocuments].sort((a, b) => -a.dateCreated.diff(b.dateCreated));
        return (
            <Container className="clinical-history">
                <h1>Clinical history</h1>
                <Form className="filters">
                    <Form.Group widths="equal">
                        <Input.DropdownMulti
                            dynamicOptions
                            label="Task Types"
                            options={[{ key: documentsFilterKey, text: "Documents", value: documentsFilterKey }, ...this.props.taskCategories.map(category =>
                                ({ key: category.name, value: category.name, text: category.name }))]}
                            onChange={(selectedTaskCategories) => this.setState({ selectedTaskCategories })}
                            value={this.state.selectedTaskCategories}
                        />
                        <Input.DropdownMulti
                            label="Product Types"
                            options={PRODUCT_TYPES.map(caseType =>
                                ({ key: caseType.value, value: caseType.value, text: caseType.displayName }))}
                            onChange={(selectedCaseTypes) => this.setState({ selectedCaseTypes })}
                            value={this.state.selectedCaseTypes}
                        />
                    </Form.Group>
                </Form>
                {this.props.isLoading && <Loader active />}
                <Item.Group divided>
                    {tasksAndDocuments.map(i => i.render)}
                </Item.Group>
            </Container>
        );
    }

    public renderTask(task: TaskModel, ohCase: OhCase, adminUserOptions: DropdownOption[]) {
        return (
            <CaseTaskCard
                key={task.id}
                task={task}
                customerId={this.props.customerId}
                ohCase={ohCase}
                employeeRecordId={this.props.employeeRecordId}
                adminUserOptions={adminUserOptions}
            />
        );
    }

    public renderCaseDocument(ohCaseDocument: OhCaseDocument, ohCase: OhCase) {
        return (
            <DocumentCard
                key={ohCaseDocument.id}
                title="Document against case"
                document={ohCaseDocument}
                downloadUrl={`/api/customer/${this.props.customerId}/case/${ohCase.id}/document`}
            >
                <Link
                    href={`/customers/${this.props.customerId}/employeeRecords/${this.props.employeeRecordId}/ohCases/${ohCase.id}`}
                    target="_blank"
                >
                    Case {ohCase.caseId} - {ohCase.caseType} Case
                </Link>
            </DocumentCard>
        );
    }

    public renderEmployeeDocument(employeeDocument: EmployeeDocument) {
        return (
            <DocumentCard
                key={employeeDocument.id}
                title="Document against employee"
                document={employeeDocument}
                downloadUrl={`/api/customer/${this.props.customerId}/employeerecord/${this.props.employeeRecordId}/document`}
            />
        );
    }
}

interface QuestionnaireState {
    questionnaires: QuestionnairesState;
}

export type ApplicationState = OhCaseState & RouterState & EmployeeRecordState & LoadingState & TaskCategoriesState & AdminUserState & QuestionnaireState;
export const mapStateToProps = (state: ApplicationState): ClinicalHistoryStateProps => {
    const employeeRecordId = state.router.params.employeeRecordId;
    const employeeRecord = state.employeeRecordsDetail.employeeRecords.find(e => e.id === employeeRecordId);
    return {
        ohCases: state.ohCases.filter(c => c.employeeRecordId === employeeRecordId),
        employeeDocuments: ((employeeRecord && "documents" in employeeRecord && employeeRecord.documents) || []),
        customerId: state.router.params.customerId,
        employeeRecordId,
        isLoading: isRequestActive(state, LOAD_OHCASE_DETAILS) || isRequestActive(state, LOAD_EMPLOYEE_DOCUMENTS),
        taskCategories: state.taskCategories,
        adminUsers: state.adminUsers
    };
};

function mapDispatchToProps(dispatch: ThunkDispatch<ApplicationState, undefined, Action>): DispatchProps {
    return {
        loadDetailedOhCases: (customerId: string, employeeRecordId: string) => dispatch(loadDetailedOhCases({ customerId, employeeRecordId })),
        loadEmployeeDocuments: (customerId: string, employeeRecordId: string) => dispatch(loadEmployeeDocuments({ customerId, employeeRecordId })),
        loadTaskCategories: () => dispatch(loadTaskCategories()),
        loadAdminUsers: () => dispatch(loadAdminUsers()),
    };
}

export const ClinicalHistory = connect(mapStateToProps, mapDispatchToProps)(ClinicalHistoryUnconnected);
